// Cross-branch constraint chips (PRD §11). The bounded navigator can't see
// the whole graph, so a background compiler pass detects tensions between
// decisions in DIFFERENT branches and pins them into every navigator call
// (and the doc). Coherence is compiled in, not derived.

import { complete } from "../api/model";
import { PRI } from "../api/scheduler";
import { parseModelJSON } from "../lib/json-repair";
import { tidyProse } from "../lib/text";
import { bus, toast } from "./bus";
import { save } from "./persistence";
import { traceParseOutcome } from "./telemetry";
import { constraintsPrompt } from "./prompts";
import { state, topBranchOf } from "./state";

let constraintTimer: ReturnType<typeof setTimeout> | null = null;
let constraintRun = 0;

function decisionsByBranch(): Record<string, string[]> {
  const groups: Record<string, string[]> = {};
  for (const n of state.nodes.values()) {
    if (n.chosenIndex == null || !n.options[n.chosenIndex]) continue;
    const b = topBranchOf(n) || "root";
    (groups[b] ||= []).push(`${n.question} → ${n.options[n.chosenIndex]!.label}`);
  }
  return groups;
}

export function scheduleConstraintScan(): void {
  if (Object.keys(decisionsByBranch()).length < 2) return; // tensions need ≥2 branches
  if (constraintTimer) clearTimeout(constraintTimer);
  constraintTimer = setTimeout(() => void scanConstraints(), 2500);   // debounce a burst of picks
}

export async function scanConstraints(): Promise<void> {
  const groups = decisionsByBranch();
  if (Object.keys(groups).length < 2) return;
  const run = ++constraintRun;
  const lines = Object.entries(groups)
    .map(([b, ds]) => `[${b}]\n${ds.map((d) => `  - ${d}`).join("\n")}`).join("\n");
  try {
    const raw = await complete(constraintsPrompt(state.idea, lines), { max: 230, temp: 0.4, priority: PRI.GROUND, kind: "constraints" });
    const { data, parse } = parseModelJSON(raw);
    traceParseOutcome("constraints", parse, raw);
    if (!data) return;
    if (run !== constraintRun) return;                     // superseded by a newer scan
    const next = (data.constraints || []).slice(0, 3).map((c: any) => ({
      text: tidyProse(String(c.text || "")).slice(0, 140),
      branches: Array.isArray(c.branches) ? c.branches.map(String).slice(0, 3) : [],
    })).filter((c: any) => c.text);
    const changed = JSON.stringify(next) !== JSON.stringify(state.constraints);
    state.constraints = next;
    save();
    if (changed) {
      bus.emit("render:constraints", {});
      if (next.length) toast(`⛓ compiler pinned ${next.length} cross-branch constraint${next.length > 1 ? "s" : ""}`);
    }
  } catch { /* advisory — skip silently */ }
}
